// frontend/lib/hooks/useNurseApplications.ts
"use client";

import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../apiClient";

// =============================================================================
// Types (matching backend response shapes)
// =============================================================================

export type ApplicationStatus =
  | "APPLIED"
  | "UNDER_REVIEW"
  | "INTERVIEW"
  | "OFFERED"
  | "HIRED"
  | "REJECTED"
  | "WITHDRAWN";

export interface ApplicationSummary {
  id: string;
  job_id: string;
  job_title: string;
  facility_name: string | null;
  status: ApplicationStatus;
}

export interface ApplicationListResponse {
  results: ApplicationSummary[];
  page: number;
  page_size: number;
  total: number;
}

export interface ApplicationComplianceIssue {
  credential_type_id: number;
  credential_type_name: string;
  reason: "MISSING" | "EXPIRED" | "STATE_MISMATCH" | "NOT_VERIFIED";
}

export interface ComplianceResult {
  is_compliant: boolean;
  missing: ApplicationComplianceIssue[];
  expired: ApplicationComplianceIssue[];
  state_mismatch: ApplicationComplianceIssue[];
  not_verified: ApplicationComplianceIssue[];
}

export type ComplianceStatus = "COMPLIANT" | "AT_RISK" | "NON_COMPLIANT";

export interface ApplicationWithCompliance extends ApplicationSummary {
  compliance?: ComplianceResult;
  complianceStatus?: ComplianceStatus;
  complianceLoading?: boolean;
}

// =============================================================================
// Applications Hook
// =============================================================================

interface UseNurseApplicationsOptions {
  statusFilter?: ApplicationStatus;
  page?: number;
  pageSize?: number;
  /** If true, fetches compliance info for each application */
  includeCompliance?: boolean;
}

export function useNurseApplications(options: UseNurseApplicationsOptions = {}) {
  const {
    statusFilter,
    page = 1,
    pageSize = 20,
    includeCompliance = false,
  } = options;

  const [applications, setApplications] = useState<ApplicationWithCompliance[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (statusFilter) {
        params.set("status", statusFilter);
      }

      const data = await apiGet<ApplicationListResponse>(
        `/applications?${params.toString()}`
      );

      // Set initial applications without compliance
      const apps: ApplicationWithCompliance[] = data.results.map((app) => ({
        ...app,
        complianceLoading: includeCompliance,
      }));
      setApplications(apps);
      setTotal(data.total);

      // Fetch compliance for each application if requested
      if (includeCompliance && apps.length > 0) {
        // Fetch all compliance checks in parallel
        const compliancePromises = apps.map(async (app) => {
          try {
            const compliance = await apiGet<ComplianceResult>(
              `/applications/${app.id}/compliance`
            );
            return { id: app.id, compliance, error: null };
          } catch (err) {
            return { id: app.id, compliance: null, error: err };
          }
        });

        const complianceResults = await Promise.all(compliancePromises);

        // Update applications with compliance data
        setApplications((prev) =>
          prev.map((app) => {
            const result = complianceResults.find((r) => r.id === app.id);
            if (result?.compliance) {
              return {
                ...app,
                compliance: result.compliance,
                complianceStatus: computeComplianceStatus(result.compliance),
                complianceLoading: false,
              };
            }
            return { ...app, complianceLoading: false };
          })
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, statusFilter, includeCompliance]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  return {
    applications,
    total,
    isLoading,
    error,
    refetch: fetchApplications,
  };
}

// =============================================================================
// Single Application Compliance Hook
// =============================================================================

export function useApplicationCompliance(applicationId: string | null) {
  const [compliance, setCompliance] = useState<ComplianceResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCompliance = useCallback(async () => {
    if (!applicationId) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await apiGet<ComplianceResult>(
        `/applications/${applicationId}/compliance`
      );
      setCompliance(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Unknown error"));
    } finally {
      setIsLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    fetchCompliance();
  }, [fetchCompliance]);

  return {
    compliance,
    complianceStatus: compliance ? computeComplianceStatus(compliance) : null,
    isLoading,
    error,
    refetch: fetchCompliance,
  };
}

// =============================================================================
// Helpers
// =============================================================================

function computeComplianceStatus(result: ComplianceResult): ComplianceStatus {
  const hasBlockingIssues = result.missing.length > 0 || result.expired.length > 0;
  const hasWarningIssues =
    result.state_mismatch.length > 0 || result.not_verified.length > 0;

  if (!result.is_compliant || hasBlockingIssues) {
    return "NON_COMPLIANT";
  }
  if (hasWarningIssues) {
    return "AT_RISK";
  }
  return "COMPLIANT";
}

export function getActiveApplicationStatuses(): ApplicationStatus[] {
  return ["APPLIED", "UNDER_REVIEW", "INTERVIEW", "OFFERED"];
}

export function isActiveStatus(status: ApplicationStatus): boolean {
  return getActiveApplicationStatuses().includes(status);
}

// =============================================================================
// React Query Hooks (for simpler use cases like job search)
// =============================================================================

/**
 * Simple React Query hook for fetching nurse applications.
 * Use this when you don't need the complex compliance-fetching logic.
 */
export function useNurseApplicationsQuery() {
  return useQuery<ApplicationListResponse>({
    queryKey: ["nurse-applications"],
    queryFn: () => apiGet<ApplicationListResponse>("/applications?page_size=100"),
    staleTime: 30_000,
  });
}

/**
 * Helper hook that returns a Set of job IDs that the nurse has already applied to.
 * Useful for disabling "Apply" buttons on job cards.
 */
export function useAppliedJobIds(): Set<string> {
  const { data } = useNurseApplicationsQuery();
  const ids = new Set<string>();
  data?.results.forEach((app) => ids.add(app.job_id));
  return ids;
}
